import Vue from 'vue';
import VueModal from 'vue-js-modal';
import DkBadge from '@v4/common/components/atoms/DkBadgeDST.vue';
import dataLayerHandler from '@v4/common/components/mixins/data-layer-handler';
import DkOpenVideo from '@v4/common/components/molecules/DkOpenVideo';
import '@v4/common/utils/lazy-load';

// mixins
import { dkModalAware } from '@v4/common/components/mixins/modal';

// misc
import { i18n, loadDefaultLanguage } from '@v4/locale/async';
import { fetchConfigurator } from '@v4/services/api/v3/car';

import { getQueryParams } from '@v4/common/utils/url';

import { LIST_SINGLE_GUIDE_PAGE } from '@v4/common/utils/data-layer';

(async () => {
    await loadDefaultLanguage();

    if (document.getElementById('header')) {
        import(/* webpackChunkName: "header" */ '@v4/header').then(({ default: Header }) => Header());
    }

    const el = document.getElementById('single-guide');

    if (!el) {
        return;
    }

    Vue.use(VueModal, { dynamic: true, injectModalsContainer: true });

    new Vue({
        el,
        name: 'SingleGuidePage',
        i18n,
        components: {
            DkBadge,
            DkOpenVideo,
            LeadFormWrapper: () => ({
                component: import(
                    /* webpackChunkName: "LeadFormWrapper-vue" */ '@v4/common/components/organisms/LeadFormWrapper/LeadFormWrapper'
                ),
            }),
            ColorSelection: () => ({
                component: import(
                    /* webpackChunkName: "ColorSelection-vue" */ '@v4/single-guide/components/molecules/ColorSelection'
                ),
            }),
            CarImage: () => ({
                component: import(/* webpackChunkName: "CarImage-vue" */ '@v4/single-guide/components/atoms/CarImage'),
            }),
            DkButton: () => ({
                component: import(/* webpackChunkName: "DkButton-vue" */ '@v4/common/components/atoms/DkButton.vue'),
            }),
            DkSlide: () => ({
                component: import(
                    /* webpackChunkName: "DkSlide-vue" */ '@v4/common/components/atoms/DkSlide/DkSlide.vue'
                ),
            }),
            DkSlider: () => ({
                component: import(
                    /* webpackChunkName: "DkSlider-vue" */ '@v4/common/components/molecules/DkSliderDST.vue'
                ),
            }),
            DkNewsCard: () => ({
                component: import(
                    /* webpackChunkName: "DkNewsCard-vue" */ '@v4/common/components/organisms/DkNewsCard.vue'
                ),
            }),
        },
        mixins: [dataLayerHandler, dkModalAware],
        data() {
            return {
                featureToggleLeadformCompact: getQueryParams().feature_toggle_leadform_compact,
                featureToggleLeadformVB: getQueryParams().feature_toggle_guide_leadform_vb,
                featureToggleLeadformVC: getQueryParams().feature_toggle_guide_leadform_vc,
                featureToggleLeadformVD: getQueryParams().feature_toggle_guide_leadform_vd,
                colorImage: '',
                colors: [],
                activeColor: null,
                formIsVisible: false,
            };
        },
        methods: {
            onSuggestedCarClick(vehicle) {
                this.pushProductClickToDataLayer(vehicle, { list: LIST_SINGLE_GUIDE_PAGE });

                const { formId, formData } = vehicle;
                if (!formId || !formData) {
                    return;
                }

                const { make, model, image, makeUrlCode, modelUrlCode } = vehicle;

                return import(
                    /* webpackChunkName: "LeadFormModal-vue" */ '@v4/single-guide/components/organisms/LeadFormModal'
                ).then(
                    async ({ default: LeadFormModal }) =>
                        await this.showModal(
                            LeadFormModal,
                            {
                                formId,
                                formData,
                                title: this.$tc('single-guide.leadform-title', `${make} ${model}`, {
                                    n: `${make} ${model}`,
                                }),
                                handleColorSelect: this.handleColorSelect,
                                fetchCarConfiguration: async () =>
                                    await this.fetchCarConfiguration(makeUrlCode, modelUrlCode),
                                defaultImage: image,
                            },
                            {
                                name: 'lead-form-modal',
                                height: 'auto',
                                classes: 'modal--fullscreen_medium',
                                width: 680,
                                scrollable: true,
                            }
                        )
                );
            },
            onVersionButtonClick(e, vehicle) {
                if (this.shouldShowDVersion()) {
                    this.onSuggestedCarClick(vehicle);
                    return;
                }

                this.colors = null;
                document
                    .querySelectorAll('.card-guide--version')
                    .forEach((el) => el.classList.remove('card-guide--version-expanded'));

                const { makeUrlCode, modelUrlCode } = vehicle;
                this.fetchCarConfiguration(makeUrlCode, modelUrlCode).then((response) => {
                    this.colors = response;
                    e.target.parentNode.classList.add('card-guide--version-expanded');
                });
            },
            closeVersionBox() {
                this.colors = null;
                this.activeColor = null;
                document
                    .querySelectorAll('.card-guide--version')
                    .forEach((el) => el.classList.remove('card-guide--version-expanded'));
            },
            handleColorSelect(color = {}) {
                color.active = true;
                this.activeColor = color;

                return this.activeColor;
            },
            async fetchCarConfiguration(makeUrlCode, submodelCommercialUrlCode) {
                try {
                    const result = await fetchConfigurator(makeUrlCode, submodelCommercialUrlCode);
                    const { data = {} } = result;
                    const { colors = {} } = data;
                    this.colors = colors.external || [];
                } catch (e) {
                    this.colors = [];
                }

                this.activeColor = null;

                return this.colors;
            },
            shouldShowDefaultVersion() {
                return (
                    (this.featureToggleLeadformCompact &&
                        !this.featureToggleLeadformVB &&
                        !this.featureToggleLeadformVC &&
                        !this.featureToggleLeadformVD) ||
                    (!this.featureToggleLeadformCompact &&
                        !this.featureToggleLeadformVB &&
                        !this.featureToggleLeadformVC &&
                        !this.featureToggleLeadformVD)
                );
            },
            shouldShowBVersion() {
                return this.featureToggleLeadformVB && !this.featureToggleLeadformVC && !this.featureToggleLeadformVD;
            },
            shouldShowCVersion() {
                return this.featureToggleLeadformVC && !this.featureToggleLeadformVB && !this.featureToggleLeadformVD;
            },
            shouldShowDVersion() {
                return this.featureToggleLeadformVD && !this.featureToggleLeadformVB && !this.featureToggleLeadformVC;
            },
        },
    });
})();
